import styled, { css, keyframes } from 'styled-components';

const stripes = keyframes`
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0% 0;
  }
`;

const animation = () =>
  css`
    ${stripes} 20s infinite linear;
  `;

// Button variant generator function
const generateButtonStyles = ({
  backgroundColor,
  borderColor,
  disabled,
  faded,
  fetching,
  size,
  textColor,
  variant,
  theme,
}) => {
  const { colors, border, font, spacing } = theme;

  const minHeight = size === 'sm' ? 36 : 48;
  const minWidth = 105;

  const baseButton = {
    ':focus, :hover, :active': {
      outline: 'none',
    },
    alignItems: 'center',
    background: 'transparent',
    backgroundRepeat: 'repeat-x',
    backgroundSize: '2000px 100%',
    border: `1px solid transparent`,
    borderRadius: border.radius.sm,
    color: colors.text[textColor] || colors.text.primary,
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex',
    fontSize: size === 'sm' ? font.size.sm : font.size.md,
    fontWeight: font.weight.bold,
    height: 48,
    justifyContent: 'center',
    lineHeight: size === 'sm' ? font.lineHeight.xs : font.lineHeight.sm,
    outline: 'none',
    padding:
      size === 'sm'
        ? `calc(${spacing.xs} * 1.16) ${spacing.sm}`
        : `calc(${spacing.sm} * 1.18) ${spacing.md}`,
    svg: {
      marginRight: size === 'sm' ? spacing.xs : spacing.sm,
    },
  };

  switch (variant) {
    case 'text':
      return {
        ...baseButton,
        border: 'none',
        borderRadius: 0,
        color: colors.secondary,
        display: 'inline-flex',
        flexDirection: 'row',
        margin: 0,
        padding: `${spacing.xxs} 0`,
      };
    case 'empty':
      return {
        ...baseButton,
        ':hover': {
          background: !disabled && !fetching && 'transparent',
        },
        background: (() => {
          if (disabled) {
            return colors.lightGrey;
          }

          if (fetching) {
            return `repeating-linear-gradient(45deg, transparent 0, transparent 10px, ${colors.lightGrey} 10px, ${colors.lightGrey} 20px)`;
          }

          return 'transparent';
        })(),
        border: 0,
        color: disabled ? colors.grey : colors.text.primary || textColor,
      };
    case 'ghost':
      return {
        ...baseButton,
        ':hover': {
          background: !disabled && !fetching && colors.lightGrey,
        },
        background: (() => {
          if (disabled) {
            return colors.lightGrey;
          }

          if (fetching) {
            return `repeating-linear-gradient(45deg, transparent 0, transparent 10px, ${colors.lightGrey} 10px, ${colors.lightGrey} 20px)`;
          }

          return 'transparent';
        })(),
        borderColor: disabled
          ? colors.grey
          : colors[borderColor] || colors.primary,
        color: disabled ? colors.grey : textColor || colors.text.secondary,
      };
    case 'primary':
      return {
        ...baseButton,
        ':focus, :hover, :active': {
          outline: 'none',
        },
        ':hover': {
          background:
            !faded &&
            !disabled &&
            !fetching &&
            (colors[`${backgroundColor}Tint`] || colors.primaryTint),
          cursor: disabled || fetching ? 'default' : 'pointer',
        },
        background: (() => {
          if (disabled) {
            return colors.disabled;
          }

          if (fetching) {
            return `repeating-linear-gradient(45deg, ${colors.primaryTint} 0, ${colors.primaryTint} 10px, ${colors.primary} 10px, ${colors.primary} 20px)`;
          }

          if (faded) {
            return `${colors[backgroundColor]}50`;
          }

          return colors[backgroundColor];
        })(),
        border: 0,
        borderRadius: 4,
        color: disabled
          ? colors.darkGrey
          : colors.text[textColor] || colors.text.primary,
        cursor: 'pointer',
        fontSize: baseButton.fontSize,
        fontWeight: '800',
        minHeight,
        minWidth,
        padding: baseButton.padding,
      };

    case 'iconAndText':
      return {
        ':focus, :hover, :active': {
          outline: 'none',
        },
        alignItems: 'center',
        background: colors.button.iconAndText.default.background,
        border: `1px solid ${colors.button.iconAndText.default.border}`,
        borderRadius: 4,
        color: colors.button.iconAndText.default.text,
        cursor: 'pointer',
        display: 'flex',
        fontSize: baseButton.fontSize,
        fontWeight: font.weight.bold,
        justifyContent: 'center',
        minHeight,
        minWidth,
        padding: baseButton.padding,
        ...(disabled
          ? {
              background: colors.button.iconAndText.disabled.background,
              border: 'none',
              color: colors.button.iconAndText.disabled.text,
              cursor: 'default',
            }
          : {
              ':hover': {
                background: colors.button.iconAndText.hover.background,
                border: `1px solid ${colors.button.iconAndText.hover.border}`,
                color: colors.button.iconAndText.hover.text,
              },
            }),
      };

    case 'stroke':
      return {
        ':focus, :hover, :active': {
          outline: 'none',
        },
        background: colors.button.stroke.default.background,
        border: `1px solid ${colors.button.stroke.default.border}`,
        borderRadius: 4,
        color: colors.button.stroke.default.text,
        fontSize: baseButton.fontSize,
        fontWeight: '800',
        minHeight,
        minWidth,
        padding: baseButton.padding,
        ...(disabled
          ? {
              background: colors.button.stroke.disabled.background,
              border: 'none',
              color: colors.button.stroke.disabled.text,
            }
          : {
              ':hover': {
                background: colors.button.stroke.hover.background,
                border: `1px solid ${colors.button.stroke.hover.border}`,
                color: colors.button.stroke.hover.text,
                cursor: 'pointer',
              },
            }),
      };

    default:
      return {
        ...baseButton,
        ':hover': {
          background:
            !faded &&
            !disabled &&
            !fetching &&
            (colors[`${backgroundColor}Tint`] || colors.primaryTint),
        },
        background: (() => {
          if (disabled) {
            return colors.disabled;
          }

          if (fetching) {
            return `repeating-linear-gradient(45deg, ${colors.primaryTint} 0, ${colors.primaryTint} 10px, ${colors.primary} 10px, ${colors.primary} 20px)`;
          }

          if (faded) {
            return `${colors[backgroundColor]}50`;
          }

          return colors[backgroundColor];
        })(),
        border: 0,
        color: disabled
          ? colors.darkGrey
          : colors.text[textColor] || colors.text.primary,
      };
  }
};

const generatedButton = styled.button(
  ({
    theme,
    backgroundColor,
    borderColor,
    disabled,
    faded,
    fetching,
    size,
    textColor,
    variant,
  }) => {
    return generateButtonStyles({
      backgroundColor,
      borderColor,
      disabled,
      faded,
      fetching,
      size,
      textColor,
      theme,
      variant,
    });
  },
);

export const StyledButton = styled(generatedButton)`
  animation: ${({ fetching }) => (fetching ? animation : '')};
`;
